import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDebounce } from '@shared/hooks/useDebounce';
import type { DateRange } from '@shared/components/FilterPopup';
import { listTechnicianApiService } from '@epics/technicianManagement/api/listTechnicianApiService';
import type { TechnicianListItem } from '@epics/technicianManagement/types/technicianListType';
import { listCertificates } from '../../api/listCertificatesApiService';
import { listCertificateEquipment } from '../../api/listCertificateEquipmentApiService';
import type {
  CertificateEquipmentOption,
  CertificateListItem,
} from '../../types/myCertificateTypes';

const ALLOWED_LIMITS = [10, 30, 50, 100] as const;
const DEFAULT_LIMIT = 10;
const OPTIONS_LIMIT = 50;
const MAX_SEARCH_LENGTH = 200;

const emptyDateRange: DateRange = { startDate: '', endDate: '' };
const emptyApplied = {
  certificateId: '',
  equipmentIds: [] as string[],
  technicianIds: [] as string[],
  dateRange: emptyDateRange,
};

const isAllowedLimit = (v: number): boolean =>
  (ALLOWED_LIMITS as readonly number[]).includes(v);

const hasDateFilter = (range: DateRange): boolean =>
  Boolean((range.startDate ?? '').trim() || (range.endDate ?? '').trim());

interface PagedFetchResult<T> {
  items: T[];
  total: number;
}

interface AsyncPagedOptions<T> {
  options: T[];
  loading: boolean;
  loadingMore: boolean;
  loadMore: () => void;
  handleSearchChange: (q: string) => void;
}

/**
 * Generic server-side-searchable, infinite-scroll options loader for filter
 * dropdowns (equipment, technician, …). `fetchPage` MUST be stable (wrap in
 * useCallback) or the effect will loop.
 */
function useAsyncPagedOptions<T>(
  fetchPage: (params: {
    page: number;
    limit: number;
    search?: string;
  }) => Promise<PagedFetchResult<T>>
): AsyncPagedOptions<T> {
  const [options, setOptions] = useState<T[]>([]);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const debouncedSearch = useDebounce(search, 500);

  useEffect(() => {
    const isFirstPage = page === 1;
    if (isFirstPage) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }

    let cancelled = false;

    const run = async () => {
      try {
        const result = await fetchPage({
          page,
          limit: OPTIONS_LIMIT,
          ...(debouncedSearch.trim() && { search: debouncedSearch.trim() }),
        });
        if (cancelled) {
          return;
        }
        setOptions((prev) =>
          isFirstPage ? result.items : [...prev, ...result.items]
        );
        setTotal(result.total);
      } catch {
        if (!cancelled && isFirstPage) {
          setOptions([]);
        }
      } finally {
        if (!cancelled) {
          if (isFirstPage) {
            setLoading(false);
          } else {
            setLoadingMore(false);
          }
        }
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [page, debouncedSearch, fetchPage]);

  // Reset to page 1 whenever the search term changes.
  useEffect(() => {
    setPage(1);
    setOptions([]);
  }, [debouncedSearch]);

  const hasMore = options.length < total;

  const loadMore = useCallback(() => {
    if (!loading && !loadingMore && hasMore) {
      setPage((prev) => prev + 1);
    }
  }, [loading, loadingMore, hasMore]);

  const handleSearchChange = useCallback((q: string) => {
    setSearch(q);
  }, []);

  return { options, loading, loadingMore, loadMore, handleSearchChange };
}

export interface UseCertificateAdminListReturn {
  items: CertificateListItem[];
  loading: boolean;
  page: number;
  setPage: (p: number) => void;
  limit: number;
  totalCount: number;
  isNoFilterFetch: boolean;
  fetchError: boolean;
  search: string;
  isSearchClearing: boolean;
  setSearch: (v: string) => void;
  equipmentIds: string[];
  setEquipmentIds: (v: string[]) => void;
  technicianIds: string[];
  setTechnicianIds: (v: string[]) => void;
  dateRange: DateRange;
  setDateRange: (r: DateRange) => void;
  appliedFilters: typeof emptyApplied;
  handleApplyFilters: () => void;
  handleClearFilters: () => void;
  limitError: string;
  handleLimitChange: (limit: number) => void;
  /** Always 'generated_at' — the only sortable column (date of issue). */
  sortColumn: 'generated_at';
  sortOrder: 'asc' | 'desc';
  handleSort: (columnKey: string) => void;
  // Equipment filter dropdown
  equipmentOptions: CertificateEquipmentOption[];
  equipmentOptionsLoading: boolean;
  equipmentOptionsLoadingMore: boolean;
  loadMoreEquipment: () => void;
  handleEquipmentSearchChange: (q: string) => void;
  // Technician filter dropdown
  technicianOptions: TechnicianListItem[];
  technicianOptionsLoading: boolean;
  technicianOptionsLoadingMore: boolean;
  loadMoreTechnicians: () => void;
  handleTechnicianSearchChange: (q: string) => void;
}

/**
 * Drives the admin / super-admin "Certyfikaty" table in CertificateList.
 *
 * Page-based pagination (Table + Pagination), unlike the technician card grid
 * which uses infinite scroll via useCertificateTabList. Search auto-commits on
 * debounce; equipment + technician + date filters stage locally until
 * handleApplyFilters. Both filter dropdowns are server-side searched with
 * infinite scroll. Sorting is by date of issue only.
 */
export function useCertificateAdminList(): UseCertificateAdminListReturn {
  const [searchParams, setSearchParams] = useSearchParams();
  const isSyncingFromUrl = useRef(false);
  const lastWrittenRef = useRef<string | null>(null);

  // ── Certificate list state ────────────────────────────────────────────────
  const [items, setItems] = useState<CertificateListItem[]>([]);
  const [page, setPage] = useState(() => {
    const v = parseInt(searchParams.get('page') ?? '1', 10);
    return Number.isNaN(v) || v < 1 ? 1 : v;
  });
  const [limit, setLimit] = useState(() => {
    const v = parseInt(searchParams.get('limit') ?? String(DEFAULT_LIMIT), 10);
    return isAllowedLimit(v) ? v : DEFAULT_LIMIT;
  });
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);
  const [limitError, setLimitError] = useState('');

  const [search, setSearchState] = useState('');
  const [equipmentIds, setEquipmentIds] = useState<string[]>([]);
  const [technicianIds, setTechnicianIds] = useState<string[]>([]);
  const [dateRange, setDateRange] = useState<DateRange>(emptyDateRange);
  const [appliedFilters, setAppliedFilters] = useState(emptyApplied);
  // Only one sortable column (date of issue); default newest-first per backend.
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const debouncedSearch = useDebounce(search, 500);
  const prevDebouncedRef = useRef('');

  // ── Page/limit ⇄ URL sync ─────────────────────────────────────────────────
  // Only page + limit live in the URL. The active `tab` param is preserved so
  // navigating pages never drops the tab; other params are intentionally not
  // written here. The highest-page fallback for an out-of-range `page` lives in
  // the main fetch below (setPage(totalPages)), which then flows back to the URL.

  // State → URL
  useEffect(() => {
    if (isSyncingFromUrl.current) {
      return;
    }
    const params = new URLSearchParams();
    const tab = searchParams.get('tab');
    if (tab) {
      params.set('tab', tab);
    }
    params.set('page', page.toString());
    params.set('limit', limit.toString());
    const str = params.toString();
    if (str === lastWrittenRef.current) {
      return;
    }
    lastWrittenRef.current = str;
    setSearchParams(params, { replace: true });
  }, [page, limit, searchParams, setSearchParams]);

  // URL → State (browser back/forward, manual edits)
  useEffect(() => {
    const str = new URLSearchParams(searchParams).toString();
    if (str === lastWrittenRef.current) {
      return;
    }
    const urlPage = parseInt(searchParams.get('page') ?? '1', 10);
    const urlLimit = parseInt(
      searchParams.get('limit') ?? String(DEFAULT_LIMIT),
      10
    );
    const nextPage = Number.isNaN(urlPage) || urlPage < 1 ? 1 : urlPage;
    const nextLimit = isAllowedLimit(urlLimit) ? urlLimit : DEFAULT_LIMIT;
    isSyncingFromUrl.current = true;
    setPage((prev) => (prev !== nextPage ? nextPage : prev));
    setLimit((prev) => (prev !== nextLimit ? nextLimit : prev));
    requestAnimationFrame(() => {
      isSyncingFromUrl.current = false;
    });
  }, [searchParams]);

  // ── Filter option loaders (stable fetchers for the generic hook) ──────────
  const fetchEquipment = useCallback(
    (p: { page: number; limit: number; search?: string }) =>
      listCertificateEquipment(p).then((d) => ({
        items: d.items,
        total: d.total,
      })),
    []
  );
  const fetchTechnicians = useCallback(
    (p: { page: number; limit: number; search?: string }) =>
      listTechnicianApiService
        .listTechnicians(p)
        .then((r) => ({ items: r.data, total: r.totalCount })),
    []
  );

  const {
    options: equipmentOptions,
    loading: equipmentOptionsLoading,
    loadingMore: equipmentOptionsLoadingMore,
    loadMore: loadMoreEquipment,
    handleSearchChange: handleEquipmentSearchChange,
  } = useAsyncPagedOptions<CertificateEquipmentOption>(fetchEquipment);

  const {
    options: technicianOptions,
    loading: technicianOptionsLoading,
    loadingMore: technicianOptionsLoadingMore,
    loadMore: loadMoreTechnicians,
    handleSearchChange: handleTechnicianSearchChange,
  } = useAsyncPagedOptions<TechnicianListItem>(fetchTechnicians);

  // ── Commit debounced search → applied filter, reset to page 1 ─────────────
  useEffect(() => {
    if (prevDebouncedRef.current === debouncedSearch) {
      return;
    }
    prevDebouncedRef.current = debouncedSearch;
    setAppliedFilters((prev) => ({ ...prev, certificateId: debouncedSearch }));
    setPage(1);
  }, [debouncedSearch]);

  // ── Certificate list: main fetch (page replaces, not accumulates) ──────────
  useEffect(() => {
    setLoading(true);
    const controller = new AbortController();
    setFetchError(false);

    const run = async () => {
      try {
        const start = (appliedFilters.dateRange.startDate ?? '').trim();
        const end = (appliedFilters.dateRange.endDate ?? '').trim();
        const result = await listCertificates({
          page,
          limit,
          ...(appliedFilters.certificateId && {
            certificateId: appliedFilters.certificateId,
          }),
          ...(appliedFilters.equipmentIds.length > 0 && {
            equipmentIds: appliedFilters.equipmentIds,
          }),
          ...(appliedFilters.technicianIds.length > 0 && {
            technicianIds: appliedFilters.technicianIds,
          }),
          ...(start && { startDate: start }),
          ...(end && { endDate: end }),
          sortOrder,
        });

        if (controller.signal.aborted) {
          return;
        }

        const totalPages = Math.ceil(result.data.total / limit);
        if (result.data.total > 0 && totalPages > 0 && page > totalPages) {
          setPage(totalPages);
          return;
        }

        setItems(result.data.items);
        setTotalCount(result.data.total);
      } catch {
        if (!controller.signal.aborted) {
          setFetchError(true);
          setItems([]);
          setTotalCount(0);
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    };

    void run();
    return () => controller.abort();
  }, [page, limit, appliedFilters, sortOrder]);

  const setSearch = useCallback(
    (next: string) => {
      const normalized = next.trimStart();
      if (normalized.length > MAX_SEARCH_LENGTH) {
        return;
      }
      if (!normalized.trim() && search.trim()) {
        setLoading(true);
      }
      setSearchState(normalized);
    },
    [search]
  );

  const handleSort = useCallback((columnKey: string) => {
    if (columnKey !== 'generated_at') {
      return;
    }
    setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    setPage(1);
  }, []);

  const handleLimitChange = useCallback((newLimit: number) => {
    if (!isAllowedLimit(newLimit)) {
      setLimitError('Dozwolone wartości: 10, 30, 50, 100.');
      return;
    }
    setLimitError('');
    setLimit(newLimit);
    setPage(1);
  }, []);

  const handleApplyFilters = useCallback(() => {
    setAppliedFilters({
      certificateId: search,
      equipmentIds,
      technicianIds,
      dateRange,
    });
    prevDebouncedRef.current = search;
    setPage(1);
  }, [search, equipmentIds, technicianIds, dateRange]);

  const handleClearFilters = useCallback(() => {
    setEquipmentIds([]);
    setTechnicianIds([]);
    setDateRange(emptyDateRange);
    setAppliedFilters((prev) => ({
      ...prev,
      equipmentIds: [],
      technicianIds: [],
      dateRange: emptyDateRange,
    }));
    setPage(1);
  }, []);

  const isNoFilterFetch = useMemo(
    () =>
      !appliedFilters.certificateId &&
      appliedFilters.equipmentIds.length === 0 &&
      appliedFilters.technicianIds.length === 0 &&
      !hasDateFilter(appliedFilters.dateRange),
    [appliedFilters]
  );

  const isSearchClearing =
    !search.trim() &&
    debouncedSearch.trim() !== '' &&
    appliedFilters.equipmentIds.length === 0 &&
    appliedFilters.technicianIds.length === 0 &&
    !hasDateFilter(appliedFilters.dateRange);

  return {
    items,
    loading,
    page,
    setPage,
    limit,
    totalCount,
    isNoFilterFetch,
    fetchError,
    search,
    isSearchClearing,
    setSearch,
    equipmentIds,
    setEquipmentIds,
    technicianIds,
    setTechnicianIds,
    dateRange,
    setDateRange,
    appliedFilters,
    handleApplyFilters,
    handleClearFilters,
    limitError,
    handleLimitChange,
    sortColumn: 'generated_at',
    sortOrder,
    handleSort,
    equipmentOptions,
    equipmentOptionsLoading,
    equipmentOptionsLoadingMore,
    loadMoreEquipment,
    handleEquipmentSearchChange,
    technicianOptions,
    technicianOptionsLoading,
    technicianOptionsLoadingMore,
    loadMoreTechnicians,
    handleTechnicianSearchChange,
  };
}
